import { useEffect, useLayoutEffect, useState, type RefObject } from "react";

/**
 * Observe an element's content-box size. Used by every grid surface to map
 * normalised zone coords to pixels once the container has laid out.
 *
 * Implementation notes:
 *   • Synchronous post-layout measurement is used ONLY to recover from a 0-size
 *     mount (lazy routes, async-loaded images, mobile flex layouts that don't
 *     have a definite height the first time the hook runs). On Brave iOS in
 *     particular the ResizeObserver doesn't always fire for the "0 → settled
 *     by next paint" transition, so we keep measuring on every render until
 *     we have a non-zero size — then we stop and let ResizeObserver own
 *     steady-state.
 *   • CRITICAL: do NOT keep the post-layout measurement running on every
 *     render once we have a real size. During fullscreen enter/exit the
 *     `getBoundingClientRect()` path and the ResizeObserver path can each
 *     report different rounded integers between frames (1920 vs 1921 as the
 *     browser animates the surface). Two competing setState sources on the
 *     same render → infinite loop → React #185 "Maximum update depth
 *     exceeded".
 *   • ResizeObserver alone covers all steady-state changes: window resize,
 *     parent flex changes, fullscreen enter/exit, devtools toggling, etc.
 *   • Rounding to integer pixels still applies in both paths to absorb
 *     sub-pixel jitter.
 */
export function useElementSize(ref: RefObject<HTMLElement | null>): { w: number; h: number } {
  const [size, setSize] = useState({ w: 0, h: 0 });

  // Mount-time / 0-size rescue only. Once we've measured a real size, this
  // bails out at the setState comparator and never schedules another render —
  // ResizeObserver below owns every change from then on.
  useLayoutEffect(() => {
    const el = ref.current;
    if (!el) return;
    const rect = el.getBoundingClientRect();
    const w = Math.round(rect.width);
    const h = Math.round(rect.height);
    setSize((prev) => {
      if (prev.w !== 0 && prev.h !== 0) return prev;
      if (prev.w === w && prev.h === h) return prev;
      return { w, h };
    });
  });

  // Steady-state updates via ResizeObserver.
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const ro = new ResizeObserver((entries) => {
      const e = entries[0];
      if (!e) return;
      const w = Math.round(e.contentRect.width);
      const h = Math.round(e.contentRect.height);
      setSize((prev) => (prev.w === w && prev.h === h ? prev : { w, h }));
    });
    ro.observe(el);
    return () => ro.disconnect();
  }, [ref]);

  return size;
}
